import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Award, Printer, ShieldCheck, ShieldX } from "lucide-react";
import { PortalHeader } from "./CitizenPay";
import { PublicCertificateDownloadButton, originParam } from "../../components/TransferCertificate";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { api, errMsg } from "../../lib/api";

/**
 * Phase 124 — public verification page for an Ownership Transfer Certificate (`/:code/certificate/:token`).
 * The token is printed on the PDF as a QR code / link; anyone scanning it sees the issued record here.
 */
export default function PublicCertificate() {
  const { code, token } = useParams();
  const [cert, setCert] = useState(null);
  const [muni, setMuni] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/t/${code}/public/transfer-certificate/${token}?${originParam()}`).then((r) => setCert(r.data)).catch((e) => setError(errMsg(e)));
    api.get(`/t/${code}/public/pay/config`).then((r) => setMuni(r.data.municipality)).catch(() => {});
  }, [code, token]);

  const p = cert?.property || {};
  const m = cert?.municipality || {};
  const staff = (name, emp, role) => (name ? `${name}${emp ? ` (${emp})` : ""}${role ? `, ${role}` : ""}` : "-");

  const Row = ({ label, value, testId, mono }) => (
    <div className="grid grid-cols-[130px_1fr] sm:grid-cols-[170px_1fr] gap-2 py-2 border-b last:border-b-0 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`font-medium break-words ${mono ? "font-mono" : ""}`} data-testid={testId}>{value || "-"}</dd>
    </div>
  );

  return (
    <div className="min-h-screen bg-muted/40 flex flex-col" data-testid="public-certificate-page">
      <div className="no-print"><PortalHeader muni={muni} code={code} /></div>
      <main className="flex-1 w-full max-w-2xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between gap-2 no-print">
          <Button variant="outline" asChild data-testid="public-certificate-back">
            <Link to={`/${code}/pay`}><ArrowLeft className="h-4 w-4 mr-2" /> Payment portal</Link>
          </Button>
          {cert && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => window.print()} data-testid="public-certificate-print"><Printer className="h-4 w-4 mr-2" /> Print</Button>
              <PublicCertificateDownloadButton code={code} token={token} certificateNumber={cert.certificate_number} size="default" testId="public-certificate-download" />
            </div>
          )}
        </div>

        {error ? (
          <Card className="shadow-sm"><CardContent className="p-8 text-center space-y-3" data-testid="public-certificate-error">
            <ShieldX className="h-10 w-10 mx-auto text-destructive" />
            <p className="font-medium">Certificate not found</p>
            <p className="text-sm text-muted-foreground">This verification link is incomplete or does not match any certificate issued by the municipality. Treat the document as unverified.</p>
          </CardContent></Card>
        ) : !cert ? (
          <Skeleton className="h-[520px] w-full rounded-xl" data-testid="public-certificate-skeleton" />
        ) : (
          <>
            <div className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${cert.valid ? "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10" : "border-destructive/40 bg-destructive/10"}`} data-testid="public-certificate-status">
              {cert.valid ? <ShieldCheck className="h-5 w-5 mt-0.5 text-[hsl(var(--success))]" /> : <ShieldX className="h-5 w-5 mt-0.5 text-destructive" />}
              <div className="text-sm">
                <p className="font-semibold">{cert.valid ? "Authentic certificate" : "Certificate no longer valid"}</p>
                <p className="text-muted-foreground">
                  {cert.valid
                    ? `Issued by ${m.name || "the municipality"} on ${cert.issued_date}. The details below are the official record.`
                    : `The underlying transfer case is ${cert.status}.`}
                </p>
              </div>
            </div>

            <Card className="shadow-sm print:shadow-none">
              <CardContent className="p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3 border-b pb-4">
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-heading font-semibold text-lg leading-tight" data-testid="public-certificate-muni">{m.name || "Municipality"}</p>
                    <p className="text-xs text-muted-foreground">{[m.address, m.district, m.state].filter(Boolean).join(" • ")}</p>
                    <p className="text-xs font-semibold mt-1">PROPERTY TAX DEPARTMENT</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-mono font-semibold" data-testid="public-certificate-number">{cert.certificate_number}</p>
                    <p className="text-xs text-muted-foreground">Issued on {cert.issued_date}</p>
                  </div>
                </div>

                <div className="text-center py-5">
                  <Award className="h-8 w-8 mx-auto text-primary mb-2" />
                  <h1 className="font-heading text-xl font-semibold text-primary">Certificate of Ownership Transfer</h1>
                  <p className="text-xs text-muted-foreground">Mutation of tax-payer name in the Property Tax Register</p>
                </div>

                <p className="text-sm leading-relaxed text-justify" data-testid="public-certificate-body">
                  This is to certify that the property bearing <b>PIN {p.pin}</b>{p.address ? `, situated at ${[p.house_no && `H.No ${p.house_no}`, p.address].filter(Boolean).join(", ")}` : ""},
                  hitherto recorded in the Property Tax Register of <b>{m.name || "the municipality"}</b> in the name of <b>{cert.old_owner_name || "-"}</b>,
                  has been transferred to and is now recorded in the name of <b>{cert.new_owner_name}</b>
                  {cert.new_father_spouse_name ? ` (${cert.new_father_spouse_name})` : ""} with effect from <b>{cert.approved_date}</b>,
                  pursuant to Transfer Case <b>{cert.transfer_id}</b> arising from field re-assessment <b>{cert.reassessment_id}</b>.
                </p>

                <dl className="mt-5 rounded-lg border px-3" data-testid="public-certificate-details">
                  <Row label="Property PIN" value={p.pin} mono testId="public-certificate-pin" />
                  <Row label="Previous owner" value={cert.old_owner_name} testId="public-certificate-old-owner" />
                  <Row label="New owner" value={cert.new_owner_name} testId="public-certificate-new-owner" />
                  <Row label="Zone / Ward" value={[p.zone_label, p.ward_label].filter(Boolean).join(" / ")} />
                  <Row label="Transfer case" value={cert.transfer_id} mono />
                  <Row label="Re-assessment" value={cert.reassessment_id} mono />
                  <Row label="Basis of transfer" value={cert.transfer_remarks || cert.reason} />
                  <Row label="Recorded by (field)" value={staff(cert.initiated_by_name, cert.initiated_by_employee_id, cert.initiated_by_role_label)} />
                  <Row label="Approved by" value={staff(cert.approved_by_name, cert.approved_by_employee_id, cert.approved_by_role_label)} testId="public-certificate-approver" />
                  <Row label="Approved on" value={cert.approved_date} />
                  {cert.rationale && <Row label="Approval note" value={cert.rationale} />}
                </dl>

                <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
                  This certificate records the change of the assessee (tax-payer) name in the municipal Property Tax Register on the basis of documents
                  produced at the time of field re-assessment. It does not by itself confer, transfer or adjudicate title to the property.
                  All outstanding property tax dues on this PIN, if any, stand payable by the recorded owner.
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
